import { Chapter, Mission, Scenario } from '../types';
import { apiFetch } from './api-client';

const COMPLETED_KEY_PREFIX = 'vn_missions_completed_';

export function getLocalCompletedMissions(scenarioId: string): string[] {
  try {
    const raw = localStorage.getItem(`${COMPLETED_KEY_PREFIX}${scenarioId}`);
    if (!raw) return [];
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? parsed.filter((id): id is string => typeof id === 'string') : [];
  } catch {
    return [];
  }
}

export function setLocalCompletedMissions(scenarioId: string, missionIds: string[]): void {
  localStorage.setItem(`${COMPLETED_KEY_PREFIX}${scenarioId}`, JSON.stringify(missionIds));
}

export function isMissionCompletedLocally(scenarioId: string, missionId: string): boolean {
  return getLocalCompletedMissions(scenarioId).includes(missionId);
}

export function markMissionCompletedLocally(scenarioId: string, missionId: string): void {
  const completed = new Set(getLocalCompletedMissions(scenarioId));
  if (completed.has(missionId)) return;
  completed.add(missionId);
  setLocalCompletedMissions(scenarioId, [...completed]);
}

export function isMissionUnlocked(
  _scenarioId: string,
  mission: Mission,
  completedMissionIds: string[]
): boolean {
  if (!mission.requiresMission) return true;
  return completedMissionIds.includes(mission.requiresMission);
}

export function filterChaptersForMission(chapters: Chapter[] | undefined, missionId?: string): Chapter[] {
  if (!chapters?.length) return [];
  if (!missionId) return chapters;
  return chapters.filter((chapter) => chapter.missionId === missionId);
}

export function getMissionLabel(missions: Mission[] | undefined, missionId?: string): string | undefined {
  if (!missionId || !missions?.length) return undefined;
  const mission = missions.find((entry) => entry.id === missionId);
  if (!mission) return undefined;
  return `Mission ${mission.order}`;
}

export function getMissionStartScene(
  scenario: Pick<Scenario, 'scenes' | 'metadata'>,
  missionId: string
): string | undefined {
  const chapters = scenario.metadata.chapters ?? [];
  const missionChapters = chapters.filter((c) => c.missionId === missionId);

  if (missionChapters.length > 0) {
    const sceneIndexById = new Map(scenario.scenes.map((s, i) => [s.id, i]));
    const sorted = [...missionChapters].sort((a, b) => {
      const ia = sceneIndexById.get(a.startScene) ?? Number.MAX_SAFE_INTEGER;
      const ib = sceneIndexById.get(b.startScene) ?? Number.MAX_SAFE_INTEGER;
      return ia - ib;
    });
    const start = sorted[0]?.startScene;
    if (start) return start;
  }

  const mission = scenario.metadata.missions?.find((m) => m.id === missionId);
  return mission?.startScene || undefined;
}

export function getMissionByChapterStartScene(
  chapters: Chapter[] | undefined,
  missions: Mission[] | undefined,
  sceneId: string
): Mission | undefined {
  const chapter = chapters?.find((entry) => entry.startScene === sceneId);
  if (chapter?.missionId) {
    return missions?.find((mission) => mission.id === chapter.missionId);
  }
  return missions?.find((mission) => mission.startScene === sceneId);
}

export function getMissionIdForScene(
  scenes: { id: string }[],
  chapters: Chapter[] | undefined,
  sceneId: string
): string | undefined {
  if (!chapters?.length || !sceneId) return undefined;

  const sceneIndexById = new Map(scenes.map((scene, index) => [scene.id, index]));
  const currentIndex = sceneIndexById.get(sceneId);
  if (currentIndex === undefined) return undefined;

  const chapterStarts = chapters
    .filter((chapter) => chapter.missionId && chapter.startScene)
    .map((chapter) => ({
      missionId: chapter.missionId!,
      startIndex: sceneIndexById.get(chapter.startScene) ?? Number.MAX_SAFE_INTEGER,
    }))
    .filter((entry) => entry.startIndex !== Number.MAX_SAFE_INTEGER)
    .sort((a, b) => a.startIndex - b.startIndex);

  let resolved: string | undefined;
  for (const chapter of chapterStarts) {
    if (chapter.startIndex <= currentIndex) {
      resolved = chapter.missionId;
    } else {
      break;
    }
  }
  return resolved;
}

export function isMissionStartScene(
  scenario: Pick<Scenario, 'metadata'>,
  sceneId: string
): boolean {
  return getMissionByChapterStartScene(
    scenario.metadata.chapters,
    scenario.metadata.missions,
    sceneId
  ) !== undefined;
}

export function resolveActiveMissionId(options: {
  missionId?: string;
  gameStateMissionId?: string;
  missions?: Mission[];
  chapters?: Chapter[];
  scenes?: { id: string }[];
  currentSceneId?: string;
}): string | undefined {
  const { missionId, gameStateMissionId, missions, chapters, scenes, currentSceneId } = options;

  if (gameStateMissionId) return gameStateMissionId;
  if (missionId) return missionId;

  if (currentSceneId && scenes?.length) {
    const resolved = getMissionIdForScene(scenes, chapters, currentSceneId);
    if (resolved) return resolved;
  }

  if (currentSceneId && chapters?.length) {
    const chapter = chapters.find((entry) => entry.startScene === currentSceneId);
    if (chapter?.missionId) return chapter.missionId;
  }

  if (currentSceneId && missions?.length) {
    const mission = getMissionByChapterStartScene(chapters, missions, currentSceneId);
    if (mission) return mission.id;
  }

  return undefined;
}

export function listLocalCompletionKeys(): { scenarioId: string; missionIds: string[] }[] {
  const results: { scenarioId: string; missionIds: string[] }[] = [];
  for (let i = 0; i < localStorage.length; i++) {
    const key = localStorage.key(i);
    if (!key?.startsWith(COMPLETED_KEY_PREFIX)) continue;
    const scenarioId = key.slice(COMPLETED_KEY_PREFIX.length);
    const missionIds = getLocalCompletedMissions(scenarioId);
    if (missionIds.length > 0) {
      results.push({ scenarioId, missionIds });
    }
  }
  return results;
}

/**
 * Align localStorage with server mission completions (server wins).
 * Call when opening the library so admin DB resets are reflected in the UI.
 */
export async function refreshLocalCompletionsFromServer(): Promise<void> {
  try {
    const response = await apiFetch('/api/stats/mission-completions');
    if (!response.ok) return;

    const data = await response.json() as { completions?: Record<string, string[]> };
    const serverCompletions = data.completions ?? {};

    const scenarioIds = new Set([
      ...listLocalCompletionKeys().map((entry) => entry.scenarioId),
      ...Object.keys(serverCompletions),
    ]);

    for (const scenarioId of scenarioIds) {
      const missionIds = serverCompletions[scenarioId] ?? [];
      if (missionIds.length === 0) {
        localStorage.removeItem(`${COMPLETED_KEY_PREFIX}${scenarioId}`);
      } else {
        setLocalCompletedMissions(scenarioId, missionIds);
      }
    }
  } catch (error) {
    console.warn('Failed to refresh mission completions from server:', error);
  }
}

export async function syncMissionCompleted(
  scenarioId: string,
  missionId: string
): Promise<void> {
  markMissionCompletedLocally(scenarioId, missionId);

  try {
    await apiFetch('/api/stats/mission-complete', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({ scenario_id: scenarioId, mission_id: missionId }),
    });
  } catch (error) {
    console.warn('Failed to sync mission completion:', error);
  }
}

/** @deprecated Prefer getMissionByChapterStartScene */
export function getMissionByStartScene(missions: Mission[], sceneId: string): Mission | undefined {
  return missions.find((mission) => mission.startScene === sceneId);
}

export function getPreviousMission(missions: Mission[], mission: Mission): Mission | undefined {
  const sorted = [...missions].sort((a, b) => a.order - b.order);
  const index = sorted.findIndex((m) => m.id === mission.id);
  return index > 0 ? sorted[index - 1] : undefined;
}

/** When navigating to a mission start scene, mark the previous mission as completed. */
export function completePreviousMissionForScene(
  scenarioId: string,
  missions: Mission[],
  chapters: Chapter[] | undefined,
  targetSceneId: string
): void {
  const targetMission = getMissionByChapterStartScene(chapters, missions, targetSceneId);
  if (!targetMission) return;
  const previous = getPreviousMission(missions, targetMission);
  if (!previous) return;
  void syncMissionCompleted(scenarioId, previous.id);
}
